import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Skeleton, message, Empty } from "antd";
import { ReactComponent as Back } from "../../../../assets/svgs/back-button.svg";
import { getApi } from "../../../../api/api_instance";
import { API_URLS } from "../../../../api/apiUrls";
import QuestionUtils from "./components/QuestionUtils";
import { SearchBar } from "../../../../components/Elements/Search/SearchBar";
import TextStyles from "../../../../components/Elements/TextStyles/TextStyles";
import { useParams } from "react-router-dom";
import { Button } from "antd";
import SectionList from "./components/SectionList";
import SubsectionList from "./components/SubSectionList";
import { ReactComponent as CollapseUp } from "../../../../assets/svgs/collapseup.svg";
import { ReactComponent as CollapseDown } from "../../../../assets/svgs/collapsedown.svg";
import { debounce } from "lodash";
import { useLocation } from "react-router-dom";
import { ArrowUpOutlined, InfoCircleOutlined } from "@ant-design/icons";

const UserAssignFormDetail = ({ standalone = true, formId: propFormId }) => {
  const navigate = useNavigate();
  const { formId: paramFormId } = useParams();
  const formId = propFormId || paramFormId;

  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [flatAnswers, setFlatAnswers] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSubsectionIndex, setCurrentSubsectionIndex] = useState(0);
  const [pageSize] = useState(100);
  const [expandAll, setExpandAll] = useState(true);
  const [showGoTop, setShowGoTop] = useState(false);
  const location = useLocation();
  const {
    formTitle,
    formName,
    projectId,
    templateId,
    employeeId,
    userId,
    id,
    userSubDocId,
    allotmentId,
  } = location.state || {};

  // Resolve IDs based on source page
  const getId = (val) => (typeof val === "object" ? val?._id : val);

  const resolvedProjectId = getId(projectId);
  const resolvedTemplateId = getId(templateId);
  const resolvedEmployeeId = getId(employeeId) || getId(userId);
  const resolvedSubDocId =
    getId(userSubDocId) || getId(allotmentId) || paramFormId;

  const scrollableContainerRef = useRef(null);

  const scrollableContainer = {
    maxHeight: "1000px",
    overflowY: "auto",
    padding: "10px",
    borderRadius: "8px",
  };

  const AnswerSummary = ({ answers }) => {
    if (!answers || Object.keys(answers).length === 0) return null;

    return (
      <div className="mb-8 p-6 bg-[#B6D4D91A] border border-[#3BA2B959] rounded-2xl">
        <div className="flex items-center gap-2 mb-4 border-b border-[#3BA2B933] pb-2">
          <InfoCircleOutlined className="text-[#06513D]" />
          <h4 className="text-[#06513D] font-bold text-base m-0 uppercase tracking-wider">
            Filled Answers Summary
          </h4>
        </div>
        <div className="space-y-4">
          {Object.entries(answers).map(([qid, ans]) => {
            if (ans && ans.default && Array.isArray(ans.default)) {
              // Table Answer
              return (
                <div
                  key={qid}
                  className="bg-white p-4 rounded-xl border border-[#3BA2B922] shadow-sm"
                >
                  <p className="text-[11px] font-bold text-gray-400 uppercase mb-2">
                    Question ID: {qid} (Table)
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50">
                          {ans.default[0]?.values &&
                            Object.keys(ans.default[0].values).map((k) => (
                              <th
                                key={k}
                                className="border p-2 font-bold text-[#06513D] uppercase"
                              >
                                {k.replace(/_/g, " ")}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ans.default.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            {row.values &&
                              Object.values(row.values).map((val, vidx) => (
                                <td key={vidx} className="border p-2">
                                  {Array.isArray(val)
                                    ? val.join(", ")
                                    : val?.toString() || "-"}
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            }
            // Text Answer
            return (
              <div
                key={qid}
                className="flex flex-col sm:flex-row sm:items-center gap-2 py-2 border-b border-gray-100 last:border-0"
              >
                <span className="text-[11px] font-bold text-[#06513D] uppercase w-48 shrink-0">
                  {qid.replace(/_/g, " ")}:
                </span>
                <span className="text-sm text-gray-700 font-medium">
                  {typeof ans === "object"
                    ? JSON.stringify(ans)
                    : ans.toString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Fetch answers for the form (client side)
  const fetchAnswers = async (section, subsection) => {
    // Use the specific standardFormAllotmentId passed in state if available,
    // otherwise fallback to other allotment IDs
    const mainAllotmentId =
      getId(location.state?.standardFormAllotmentId) ||
      getId(allotmentId) ||
      getId(id) ||
      paramFormId;
    const empId = resolvedEmployeeId;

    if (!mainAllotmentId || !section || !subsection || !empId) return;

    try {
      const response = await getApi({
        apiUrl: API_URLS.GET_CLIENT_FORM_ANSWER(
          mainAllotmentId,
          section,
          subsection,
          empId,
        ),
      });

      if (response.success && response.data) {
        let combinedMap = {};
        response.data.forEach((item) => {
          if (item.answers && Array.isArray(item.answers)) {
            // Use a local helper to flatten each item and merge
            const itemMap = {};
            const localFlatten = (list, prefix = "") => {
              if (!list || !Array.isArray(list)) return;
              list.forEach((ans) => {
                const bId = ans.questionId || ans.questionID;
                if (!bId) return;
                const valToLog =
                  ans.value !== undefined ? ans.value : ans.answer;
                console.log(
                  `Processing Answer: ${bId}, Type: ${ans.questionType}, Value:`,
                  valToLog,
                );
                let mId = bId;
                if (prefix) {
                  if (bId.includes(":::")) {
                    const [iP, oP] = bId.split(":::");
                    mId = `${iP}${prefix}:::${oP}`;
                  } else {
                    mId = `${bId}${prefix}`;
                  }
                }

                if (ans.questionType === "table") {
                  // Aggressively extract all fields from all rows into suffixed keys for pre-selection
                  if (ans.tableRows && Array.isArray(ans.tableRows)) {
                    ans.tableRows.forEach((row) => {
                      if (row.values) {
                        Object.entries(row.values).forEach(
                          ([fieldId, fieldVal]) => {
                            const prefixedKey = `${mId}_${fieldId}`;
                            [prefixedKey, fieldId].forEach((k) => {
                              if (itemMap[k] === undefined) {
                                itemMap[k] = fieldVal;
                              } else {
                                const current = Array.isArray(itemMap[k])
                                  ? itemMap[k]
                                  : [itemMap[k]];
                                const next = Array.isArray(fieldVal)
                                  ? fieldVal
                                  : [fieldVal];
                                itemMap[k] = Array.from(
                                  new Set([...current, ...next]),
                                );
                              }
                            });
                          },
                        );
                      }
                    });
                  }

                  // Standard table data mapping (categorized)
                  const tableData = {};
                  ans.tableRows?.forEach((row) => {
                    const itemType = Array.isArray(row.values?.item_type)
                      ? row.values.item_type[0]
                      : "default";
                    if (!tableData[itemType]) tableData[itemType] = [];
                    tableData[itemType].push(row);
                  });
                  itemMap[mId] = tableData;
                } else if (
                  (ans.value &&
                    typeof ans.value === "object" &&
                    ans.value.details) ||
                  (ans.details && Array.isArray(ans.details))
                ) {
                  const details = ans.value?.details || ans.details;
                  const tripCount =
                    ans.value?.number_of_trip !== undefined
                      ? ans.value.number_of_trip
                      : ans.number_of_trip !== undefined
                        ? ans.number_of_trip
                        : ans.value !== undefined
                          ? ans.value
                          : 0;

                  // Set the top-level question value (the trip count)
                  itemMap[mId] = tripCount;

                  details.forEach((d, idx) => {
                    Object.keys(d).forEach((k) => {
                      if (Array.isArray(d[k])) {
                        const suffix = `_trip_${idx}`;
                        localFlatten(d[k], suffix);
                      }
                    });
                  });
                } else {
                  // Handle array merging for multiselect/repeater values to prevent overwriting
                  if (ans.value !== undefined || ans.answer !== undefined) {
                    const newVal =
                      ans.value !== undefined ? ans.value : ans.answer;
                    const currentVal = itemMap[mId];

                    if (currentVal === undefined) {
                      itemMap[mId] = newVal;
                    } else {
                      // Merge both into an array if they aren't already
                      const currentArray = Array.isArray(currentVal)
                        ? currentVal
                        : [currentVal];
                      const newArray = Array.isArray(newVal)
                        ? newVal
                        : [newVal];
                      itemMap[mId] = Array.from(
                        new Set([...currentArray, ...newArray]),
                      );
                    }
                  } else {
                    itemMap[mId] =
                      itemMap[mId] !== undefined ? itemMap[mId] : "";
                  }
                }
              });
            };
            localFlatten(item.answers);
            console.log("UserAssignFormDetail - ItemMap for item:", itemMap);
            Object.keys(itemMap).forEach((key) => {
              if (combinedMap[key] === undefined) {
                combinedMap[key] = itemMap[key];
              } else {
                if (
                  typeof itemMap[key] !== "object" ||
                  Array.isArray(itemMap[key])
                ) {
                  const current = Array.isArray(combinedMap[key])
                    ? combinedMap[key]
                    : [combinedMap[key]];
                  const next = Array.isArray(itemMap[key])
                    ? itemMap[key]
                    : [itemMap[key]];
                  combinedMap[key] = Array.from(new Set([...current, ...next]));
                } else {
                  combinedMap[key] = itemMap[key];
                }
              }
            });
          }
        });

        console.log(
          "UserAssignFormDetail - Final Combined Map Keys:",
          Object.keys(combinedMap),
        );
        // PATCH: fix corrupted scope3_categories stored as "yes" instead of ["capital_goods"]
        // This happens when legacy data was saved with the wrong value format.
        if (
          combinedMap["scope3_categories"] === "yes" &&
          (combinedMap["scope3_categories_capital_goods"] !== undefined ||
            combinedMap["scope3_categories_capital_goods_table"] !== undefined)
        ) {
          combinedMap["scope3_categories"] = ["capital_goods"];
        }

        setFlatAnswers((prev) => ({ ...prev, ...combinedMap }));
        return response.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching answers", error);
    }
  };

  useEffect(() => {
    console.log("UserAssignFormDetail - IDs Check:", {
      projectId: resolvedProjectId,
      templateId: resolvedTemplateId,
      employeeId: resolvedEmployeeId,
      subDocId: resolvedSubDocId,
    });
    console.log("UserAssignFormDetail - State Data:", location.state);

    fetchSections();

    const handleScroll = () => {
      setShowGoTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [paramFormId, resolvedTemplateId]);

  // Removed old useEffect that fetched all answers at once

  useEffect(() => {
    const handleContainerScroll = () => {
      if (scrollableContainerRef.current) {
        const scrollTop = scrollableContainerRef.current.scrollTop;
        setShowGoTop((prev) => prev || scrollTop > 200);
      }
    };

    const containerElement = scrollableContainerRef.current;
    if (containerElement) {
      containerElement.addEventListener("scroll", handleContainerScroll);
      return () =>
        containerElement.removeEventListener("scroll", handleContainerScroll);
    }
  }, [questions, selectedSubsection]);

  const isEmployee = location.pathname.startsWith("/employee");

  const fetchSections = async () => {
    setLoading(true);
    try {
      let apiUrl = API_URLS.GET_CLIENT_ASSIGNED_SECTIONS(
        resolvedProjectId,
        resolvedTemplateId,
        resolvedEmployeeId,
        resolvedSubDocId,
      );

      const response = await getApi({ apiUrl }, true);
      console.log("UserAssignFormDetail - RAW API DATA:", response.data);

      if (response.success) {
        const sectionsData = response.data.sections || response.data || [];
        const formattedSections = sectionsData.map((s) => ({
          sectionId: s.sectionID || s.sectionId || s._id,
          name: s.title || s.name || s.sectionID,
        }));

        if (formattedSections.length > 0) {
          setSections(formattedSections);
          setSelectedSection(formattedSections[0].sectionId);
          await fetchSubsections(formattedSections[0].sectionId);
        }
      } else {
        console.error("Failed to fetch sections");
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsections = async (sectionId) => {
    setLoading(true);
    try {
      let apiUrl = API_URLS.GET_CLIENT_ASSIGNED_SUBSECTIONS(
        resolvedProjectId,
        resolvedTemplateId,
        resolvedEmployeeId,
        resolvedSubDocId,
        sectionId,
      );

      const response = await getApi({ apiUrl });

      if (response.success) {
        const subsectionsData =
          response.data.subsections || response.data || [];
        const formattedSubsections = subsectionsData.map((s) => ({
          subsectionId: s.id || s.subsectionId || s.subsectionID || s._id,
          sectionID: s.sectionID,
          name: s.name || s.title,
        }));

        setSubsections(formattedSubsections);
        setSelectedSection(sectionId);

        if (formattedSubsections.length > 0) {
          const firstSubsection = formattedSubsections[0];
          setSelectedSubsection(firstSubsection.subsectionId);
          setCurrentSubsectionIndex(0);

          // Fetch answers first, then questions to ensure pre-filling
          const answersData = await fetchAnswers(
            sectionId,
            firstSubsection.subsectionId,
          );
          await fetchQuestions(
            sectionId,
            firstSubsection.subsectionId,
            answersData,
          );
        } else {
          setQuestions([]);
        }
      } else {
        console.error("Failed to fetch subsections");
        setSubsections([]);
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
      setSubsections([]);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (sectionId, subsectionId, currentAnswers) => {
    const section = sectionId || selectedSection;
    const subsection = subsectionId || selectedSubsection;

    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: isEmployee
          ? API_URLS.GET_EMPLOYEE_STANDARD_QUESTIONS_FROM_SUBSECTION(
              section,
              resolvedTemplateId,
              subsection,
            )
          : API_URLS.GET_GET_STANDARD_FORM_Questions(
              resolvedTemplateId,
              section,
              subsection,
            ),
      });

      if (response.success) {
        let divisions = response.data.divisions || [];

        // Use the flatAnswers we just fetched
        // We use a local map here to ensure we have the latest data before state updates
        const localMap = {};
        const buildLocalMap = (ansList) => {
          if (!ansList || !Array.isArray(ansList)) return;
          ansList.forEach((a) => {
            const qid = a.questionId || a.questionID;
            if (!qid) return;
            localMap[qid] = a;

            // Aggressively extract all fields from all rows into localMap for enhancement
            if (a.questionType === "table" && a.tableRows?.length > 0) {
              a.tableRows.forEach((row) => {
                if (row.values) {
                  Object.keys(row.values).forEach((k) => {
                    const fieldVal = row.values[k];
                    // Map to both global and potentially prefixed keys in localMap
                    if (!localMap[k]) {
                      localMap[k] = { questionId: k, value: fieldVal };
                    }
                  });
                }
              });
            }

            // Recursive extraction for trips
            const value = a.value || a.answer;
            if (value && typeof value === "object" && value.details) {
              value.details.forEach((d) => {
                Object.values(d).forEach((v) => {
                  if (Array.isArray(v)) buildLocalMap(v);
                });
              });
            } else if (a.details && Array.isArray(a.details)) {
              a.details.forEach((d) => {
                Object.values(d).forEach((v) => {
                  if (Array.isArray(v)) buildLocalMap(v);
                });
              });
            }
          });
        };

        if (currentAnswers) {
          currentAnswers.forEach((sub) => {
            buildLocalMap(sub.answers);
          });
        }

        // PATCH for corrupted backend data caused by previous prefix collision:
        // If scope3_categories was overwritten by "yes" from scope3_categories_capital_goods, we restore it to "capital_goods" if the table exists
        if (
          localMap["scope3_categories"] &&
          localMap["scope3_categories"].value === "yes" &&
          (localMap["scope3_categories_capital_goods"] ||
            localMap["scope3_categories_capital_goods_table"])
        ) {
          localMap["scope3_categories"].value = ["capital_goods"];
          if (localMap["scope3_categories"].answer !== undefined) {
            localMap["scope3_categories"].answer = ["capital_goods"];
          }
        }

        const enhancedDivisions = divisions.map((div) => ({
          ...div,
          questions: div.questions.map((q) => {
            const qId = q.questionId || q.questionID;
            const savedAns = localMap[qId];
            if (savedAns) {
              const isTable =
                q.questionType === "table" || q.fieldType === "table";
              const tableData = savedAns.tableRows || [];

              // Determine the actual value to inject
              let regularVal;

              // Check for nested trip count in value object (Employee/New format)
              if (
                savedAns.value &&
                typeof savedAns.value === "object" &&
                savedAns.value.number_of_trip !== undefined
              ) {
                regularVal = savedAns.value.number_of_trip;
              }
              // Check for trip count in details array (Old format)
              else if (savedAns.details && Array.isArray(savedAns.details)) {
                regularVal = savedAns.number_of_trip || savedAns.value || 0;
              }
              // Standard values
              else {
                regularVal =
                  savedAns.value !== undefined
                    ? savedAns.value
                    : savedAns.answer !== undefined
                      ? savedAns.answer
                      : "";
              }

              return {
                ...q,
                value: isTable ? { default: tableData } : regularVal,
                answer: isTable ? { default: tableData } : regularVal,
                tableRows: tableData,
              };
            }

            return q;
          }),
        }));

        setQuestions(enhancedDivisions);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowGoTop(false);
  };

  const handleSubsectionClick = async (subsectionId) => {
    const index = subsections.findIndex((s) => s.subsectionId === subsectionId);
    if (index !== -1) {
      setCurrentSubsectionIndex(index);
    }
    setSelectedSubsection(subsectionId);
    const answersData = await fetchAnswers(selectedSection, subsectionId);
    await fetchQuestions(selectedSection, subsectionId, answersData);
  };

  const handlePrevious = async () => {
    if (currentSubsectionIndex > 0) {
      const newIndex = currentSubsectionIndex - 1;
      const prevSub = subsections[newIndex];
      setCurrentSubsectionIndex(newIndex);
      setSelectedSubsection(prevSub.subsectionId);
      const answersData = await fetchAnswers(
        selectedSection,
        prevSub.subsectionId,
      );
      await fetchQuestions(selectedSection, prevSub.subsectionId, answersData);
      scrollToTop();
    }
  };

  const handleNext = async () => {
    const isLast = currentSubsectionIndex >= subsections.length - 1;

    if (!isLast) {
      const newIndex = currentSubsectionIndex + 1;
      const nextSub = subsections[newIndex];
      setCurrentSubsectionIndex(newIndex);
      setSelectedSubsection(nextSub.subsectionId);
      const answersData = await fetchAnswers(
        selectedSection,
        nextSub.subsectionId,
      );
      await fetchQuestions(selectedSection, nextSub.subsectionId, answersData);
      scrollToTop();
    } else {
      message.success("Subsection navigation complete.");
    }
  };

  return (
    <div
      className={
        standalone
          ? "min-h-screen bg-[#F9FBFC] p-6 md:p-8 space-y-8 animate-fadeIn"
          : "space-y-6"
      }
    >
      {standalone && (
        <>
          <div className="flex justify-end text-xs font-light h-4 ">
            <p>Home</p>
            <span>/</span>
            <TextStyles className="text-[#06513D]">Question Bank</TextStyles>
            <span>/</span>
            <TextStyles className="text-[#06513D]">All Questions</TextStyles>
          </div>
          <div className="flex justify-between h-12 pb-2">
            <div className="flex items-center">
              <Back className="cursor-pointer " onClick={() => navigate(-1)} />
              <h3 className="text-[#06513D] mt-1 text-xl">{formName}</h3>
            </div>
          </div>
        </>
      )}

      <div className="w-full bg-[#fff] rounded-t-[10px] shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <div className="grid grid-cols-5 border border-[#3BA2B959] rounded-t-[10px] overflow-hidden p-1 max-h-[150px] overflow-y-auto">
          {sections.length > 0 ? (
            sections.map((section, index) => (
              <div
                key={index}
                className={`
                  relative
                  h-10
                  px-2
                  flex
                  items-center
                  justify-center
                  text-[13px]
                  font-medium
                  cursor-pointer
                  rounded-xl
                  mx-1
                  my-1
                  transition-all
                  duration-200
                  ease-in-out
                  border border-[#3BA2B959]
                  ${
                    selectedSection === section.sectionId
                      ? "bg-[#06513D] text-white shadow-md transform scale-105"
                      : "bg-transparent text-gray-700 hover:text-gray-900"
                  }
                `}
                onClick={() => fetchSubsections(section.sectionId)}
              >
                <span className="text-center leading-tight">
                  {section.name}
                </span>
              </div>
            ))
          ) : (
            <div className="col-span-5 flex items-center justify-center h-20">
              <p className="text-gray-500 text-center">No sections available</p>
            </div>
          )}
        </div>

        {selectedSection && (
          <div>
            <div className="flex flex-wrap gap-5 p-2 text-[11px] font-normal border border-[#3BA2B91C] shadow-[0_0_25.2px_0_#3BA2B91F]">
              {subsections.length > 0 ? (
                subsections.map((sub) => {
                  const isSelected = selectedSubsection === sub.subsectionId;

                  return (
                    <div
                      key={sub.subsectionId}
                      className={`p-3 rounded-xl cursor-pointer border transition-all duration-200 
                        ${
                          isSelected
                            ? "shadow-md bg-gray-100"
                            : "bg-transparent text-black"
                        }`}
                      onClick={() => handleSubsectionClick(sub.subsectionId)}
                    >
                      {sub.name}
                    </div>
                  );
                })
              ) : (
                <div className="w-full flex justify-center">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    imageStyle={{ height: 35 }}
                    description={
                      <span className="text-xs text-gray-500">No data</span>
                    }
                    style={{ margin: "8px 0" }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#fff] w-full border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6 relative">
        <div className="mt-4 ml-3">
          <div className="flex flex-col md:flex-row items-center justify-between w-[90%] m-auto gap-4">
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <p className="h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-sm font-light w-72">
                TOTAL QUESTIONS: {questions?.length || 0}
              </p>
              <SearchBar
                width="100%"
                onSearch={(value) => setSearchTerm(value)}
                className="sm:w-[250px] md:w-[300px]"
              />
            </div>

            <Button
              type="primary"
              style={{ background: "#06513D" }}
              className="text-[13px] font-light px-6 py-5"
              onClick={() => setExpandAll((prev) => !prev)}
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </Button>
          </div>

          <div style={scrollableContainer} ref={scrollableContainerRef}>
            {loading ? (
              <div className="flex flex-col gap-4 p-4">
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
              </div>
            ) : selectedSubsection ? (
              <div className="mx-20 shadow-[0_0_16px_0_#3BA2B91F]">
                {questions.length > 0 ? (
                  <QuestionUtils
                    isEmployee={false}
                    questions={questions}
                    expandAll={expandAll}
                    answers={flatAnswers}
                    setAnswers={setFlatAnswers}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    canGoPrevious={currentSubsectionIndex > 0}
                    canGoNext={currentSubsectionIndex < subsections.length - 1}
                    formAllotmentId={resolvedSubDocId}
                    templateId={resolvedTemplateId}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    imageStyle={{ height: 40 }}
                    description={
                      <span className="text-xs text-gray-500">
                        No questions available for this subsection.
                      </span>
                    }
                    style={{ padding: "16px 0" }}
                  />
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center p-8">
                Please select a subsection to view questions.
              </p>
            )}

            {showGoTop && questions.length > 0 && (
              <Button
                type="primary"
                shape="circle"
                icon={<ArrowUpOutlined />}
                size="large"
                onClick={scrollToTop}
                style={{
                  position: "absolute",
                  bottom: "30px",
                  right: "60px",
                  background: "#06513D",
                  zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAssignFormDetail;
